import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '../lib/cn';

export type InputProps = InputHTMLAttributes<HTMLInputElement> & {
  invalid?: boolean;
  valid?: boolean;
};

// Ring colors use color-mix (not `ring-<color>/<n>`): the theme colors are hex
// CSS vars, so the Tailwind opacity modifier compiles to invalid CSS and the
// ring renders nothing. color-mix keeps the intended translucency.
const base =
  'block w-full h-11 px-3.5 bg-surface border border-border rounded-md text-[15px] text-fg outline-none transition-[border-color,box-shadow,background] placeholder:text-fg-faint focus-visible:border-fg focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--color-fg)_12%,transparent)] disabled:cursor-not-allowed disabled:opacity-60';

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, invalid, valid, ...rest }, ref) => (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        base,
        invalid &&
          'border-brand focus-visible:border-brand focus-visible:ring-[color-mix(in_srgb,var(--color-brand)_22%,transparent)]',
        // Solid token via arbitrary value: `border-success/40` applies an opacity
        // modifier to a hex CSS var (renders nothing), and a bare `border-success`
        // would collide with Bootstrap's `.border-success !important`.
        valid && 'border-[var(--color-success)]',
        className,
      )}
      {...rest}
    />
  ),
);
Input.displayName = 'Input';
